<script setup lang="ts">
import DialogCross from "@/components/shared/DialogCross.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useSettingsStore } from "@/stores/settingsStore";
import type { TJobDialogMetaData } from "@/api/models/patient/patient";
import DialogIcon from "@/components/shared/DialogIcon.vue";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import SecondaryButton from "@/components/shared/buttons/SecondaryButton.vue";
import type { ButtonTone } from "@/components/shared/buttons/types";

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    title?: string;
    subtitle?: string;
    acceptAction?: () => void | Promise<void>;
    cancelAction?: () => void | Promise<void>;
    closeAction?: () => void | Promise<void>;
    actionName?: string;
    acceptText?: string;
    cancelText?: string;
    openWithParent?: boolean;
    size?: "small" | "medium" | "large";
    loading?: boolean;
    jobMetaData?: TJobDialogMetaData;
    delete?: boolean;
    /**
     * Overrides the icon only — the destructive red treatment from `delete` is kept. For confirms
     * that are irreversible but aren't deletions (signing out, discarding a session), where a
     * trash can would misdescribe what happens.
     */
    icon?: string;
    /** Defaults to true — dialogs always show regardless of the user's skip-confirmations setting. Set to false to opt this dialog into skipping. */
    alwaysConfirm?: boolean;
  }>(),
  {
    alwaysConfirm: true,
    // Resolved in `configData` instead, where the fallback depends on `delete`.
    icon: undefined,
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  close: [];
}>();

const configData = computed<{
  icon: string;
  backgroundColor: string;
  buttonTone: ButtonTone;
}>(() => {
  if (props.delete) {
    return {
      icon: props.icon ?? "mdi-delete",
      backgroundColor: "rgb(var(--v-theme-error))",
      buttonTone: "danger",
    };
  }
  return {
    icon: props.icon ?? "mdi-check",
    backgroundColor: "rgb(var(--v-theme-secondary))",
    buttonTone: "brand",
  };
});

const { t } = useI18n();

const dialogOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});
const useVModel = !props.openWithParent;
const settingsStore = useSettingsStore();

watch(dialogOpen, async (opened) => {
  if (!opened) return;
  if (props.alwaysConfirm || props.delete || !settingsStore.skipConfirmationDialogs) return;
  try {
    await props.acceptAction?.();
  } catch (error) {
    console.error("Accept action failed:", error);
    return;
  }
  dialogOpen.value = false;
});

const localLoading = ref(false);

const handleClose = () => {
  // When X is clicked, call closeAction if provided, otherwise emit close event
  if (props.closeAction) {
    props.closeAction();
  } else {
    emit("close");
  }
};

const handleAccept = async () => {
  if (typeof props.acceptAction === "function") {
    await props.acceptAction();
  }
};

const handleCancel = async () => {
  if (typeof props.cancelAction === "function") {
    await props.cancelAction();
  }
};

const handleAcceptClick = async (isActive: { value: boolean }) => {
  localLoading.value = true;

  try {
    await handleAccept();
    isActive.value = false;
  } catch (error) {
    console.error("Accept action failed:", error);
  } finally {
    localLoading.value = false;
  }
};

const handleCancelClick = async (isActive: { value: boolean }) => {
  localLoading.value = true;

  try {
    await handleCancel();
    isActive.value = false;
  } catch (error) {
    console.error("Cancel action failed:", error);
  } finally {
    localLoading.value = false;
  }
};

const createCloseHandler = (isActive: any) => {
  return () => {
    handleClose();
    isActive.value = false;
  };
};

const handleDialogModelUpdate = (value: boolean) => {
  dialogOpen.value = value;
};

const dialogListeners = computed(() => {
  if (useVModel) {
    return {
      "update:model-value": handleDialogModelUpdate,
    };
  }
  return {};
});

const dialogTitle = computed(() => {
  if (props.title) {
    return props.title;
  }
  if (props.jobMetaData?.title) {
    return props.jobMetaData.title;
  }
  return t("generic.dialog.confirmMessage.title");
});

const dialogSubtitle = computed(() => {
  if (props.subtitle) {
    return props.subtitle;
  }
  if (props.jobMetaData?.subtitle) {
    return props.jobMetaData.subtitle;
  }
  if (props.actionName) {
    return t("generic.dialog.confirmMessage.subtitleWithAction", { action: props.actionName });
  }
  return t("generic.dialog.confirmMessage.subtitle");
});

const dialogAcceptText = computed(() => {
  if (props.acceptText) {
    return props.acceptText;
  }
  if (props.jobMetaData?.acceptText) {
    return props.jobMetaData.acceptText;
  }

  return t("generic.action.confirm");
});

const dialogCancelText = computed(() => {
  if (props.cancelText) {
    return props.cancelText;
  }

  if (props.jobMetaData?.cancelText) {
    return props.jobMetaData.cancelText;
  }

  return t("generic.action.cancel");
});
</script>

<template>
  <v-dialog
    v-bind="useVModel ? { 'model-value': dialogOpen } : {}"
    v-on="dialogListeners"
    :max-width="props.size === 'medium' ? '600px' : props.size === 'large' ? '800px' : '500px'"
    :activator="props.openWithParent ? 'parent' : undefined"
    :aria-label="typeof props.modelValue"
    data-testid="confirm-dialog"
  >
    <template v-slot:default="{ isActive }">
      <dialog-icon
        :icon="configData.icon"
        :background-color="configData.backgroundColor"
      />

      <v-card
        rounded="xl"
        class="tw:max-md:!py-8 tw:relative tw:lg:!p-6"
      >
        <DialogCross :closeDialog="createCloseHandler(isActive)" />
        <v-card-title class="tw:!mt-10 tw:text-center tw:!text-2xl tw:!font-bold">
          {{ dialogTitle }}
        </v-card-title>
        <v-card-text class="tw:text-center tw:!text-lg">
          <div
            class="tw:text-md tw:lg:!px-6"
            v-if="$slots.text"
          >
            <slot name="text" />
          </div>
          <div v-else>
            {{ dialogSubtitle }}
          </div>
        </v-card-text>
        <v-card-actions class="tw:!justify-center">
          <secondary-button
            @click="handleCancelClick(isActive)"
            :loading="loading || localLoading"
            :tone="configData.buttonTone"
            >{{ dialogCancelText }}
          </secondary-button>
          <primary-button
            v-if="props.acceptAction"
            @click="handleAcceptClick(isActive)"
            :loading="loading || localLoading"
            :tone="configData.buttonTone"
            >{{ dialogAcceptText }}
          </primary-button>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>
